import { InternalRefetchQueriesInclude, useMutation, useQuery } from "@apollo/client";
import { Skeleton, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DataGrid } from "components/DataGrid";
import { gql } from "generated-graphql";
import { CredentialForDeleteQuery } from "generated-graphql/graphql";
import { usePaginationModel } from "hooks/usePaginationModel";
import pluralize from "pluralize";
import { CREDENTIALS, DELETE_CREDENTIAL } from "routes/Customer/Credentials/schema";

/**
 * Its own query rather than `GET_CREDENTIAL`, which carries the decrypted password and
 * security answers this dialog never shows.
 */
const CREDENTIAL_FOR_DELETE = gql(`
  query CredentialForDelete($id: ID!, $facilitiesPage: Int, $facilitiesPageSize: Int) {
    credential(id: $id, facilitiesPage: $facilitiesPage, facilitiesPageSize: $facilitiesPageSize) {
      id
      name
      facilityCount
      facilities {
        id
        name
        city
        state
        tenant {
          id
          name
        }
      }
    }
  }
`);

type AffectedFacility = NonNullable<CredentialForDeleteQuery["credential"]["facilities"]>[number];

function affectedFacilityColumns(showOrganization: boolean): GridColDef<AffectedFacility>[] {
  return [
    ...(showOrganization
      ? [
          {
            field: "organization",
            headerName: "Organization",
            flex: 1,
            sortable: false,
            valueGetter: (_: unknown, row: AffectedFacility) => row.tenant?.name ?? "",
          },
        ]
      : []),
    { field: "name", headerName: "Name", flex: 1, sortable: false },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      sortable: false,
      valueGetter: (_, row) => [row.city, row.state].join(", "),
    },
  ];
}

const SKELETON = (
  <Stack>
    <Skeleton variant="text" sx={{ width: "30rem" }} />
    <Skeleton variant="text" sx={{ width: "25rem" }} />
    <Skeleton variant="text" sx={{ width: "30rem" }} />
  </Stack>
);

export function ConfirmDeleteCredential({
  credentialId,
  onClose,
  refetchQueries = [CREDENTIALS],
  showOrganization = false,
}: {
  credentialId: string;
  onClose: () => void;
  /** The grid to refresh once the delete lands — each surface lists credentials its own way. */
  refetchQueries?: InternalRefetchQueriesInclude;
  /**
   * Cross-tenant surfaces only. A shared credential's facilities span organizations, and
   * the delete cascades to every link, so staff need to see whose it is before confirming.
   */
  showOrganization?: boolean;
}) {
  const alerts = useAlerts();
  const [paginationModel, setPaginationModel] = usePaginationModel();

  const { data, previousData, loading, error } = useQuery(CREDENTIAL_FOR_DELETE, {
    // `Credential.facilities` takes no arguments, so every reader of it shares one cache
    // entry. Writing a page of ten into it would truncate the editor's full list, which is
    // what that dialog derives `facilityIds` from.
    fetchPolicy: "no-cache",
    variables: {
      id: credentialId,
      facilitiesPage: paginationModel.page,
      facilitiesPageSize: paginationModel.pageSize,
    },
  });

  const [deleteCredential, { loading: isDeleting }] = useMutation(DELETE_CREDENTIAL);

  const credential = data?.credential ?? previousData?.credential;

  const affected = (
    <Stack gap={2}>
      {(credential?.facilityCount ?? 0) > 0 && (
        <>
          <Typography>
            Deleting the credential "{credential?.name}" will affect {credential?.facilityCount}{" "}
            {pluralize("facility", credential?.facilityCount ?? 0)}.
          </Typography>
          <DataGrid<AffectedFacility>
            loading={loading}
            rowCount={credential?.facilityCount ?? 0}
            rows={credential?.facilities ?? []}
            columns={affectedFacilityColumns(showOrganization)}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
          />
        </>
      )}
    </Stack>
  );

  function info() {
    if (error) {
      return (
        <Typography color="error">
          This credential could not be loaded, so the facilities a delete would affect can't be
          listed.
        </Typography>
      );
    }
    return credential ? affected : SKELETON;
  }

  return (
    <ConfirmDialog
      open={true}
      header="Are you sure you want to delete this credential?"
      resources={credential ? [{ resourceType: "Credential", resourceName: credential.name }] : []}
      info={info()}
      loading={isDeleting}
      // Nothing to confirm until the consequences are on screen.
      disableConfirm={!credential}
      onClose={onClose}
      onConfirm={() =>
        deleteCredential({
          variables: { id: credentialId },
          onCompleted() {
            onClose();
            alerts.success("Credential deleted successfully!");
          },
          onError(e) {
            alerts.error("There was an error deleting the credential:", e);
          },
          refetchQueries,
        })
      }
    />
  );
}
